"use client";

import { useMemo, useState } from "react";
import {
  Notice,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { CARD, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { BarChart } from "@/components/tools/stats-distributions/charts";
import {
  DistributionTable,
  ProbabilitySummary,
} from "@/components/tools/stats-distributions/discrete-parts";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput, parseNumberInput } from "@/lib/parse-number";
import { normalCdf } from "@/lib/stats/distributions";
import { barGeometry } from "@/lib/tool-kit/stats-distributions/chart";
import {
  type DiscreteModel,
  discreteProbabilities,
  distributionTable,
  poissonModel,
  tableRange,
} from "@/lib/tool-kit/stats-distributions/discrete";
import type { NumberFormatter } from "@/lib/tool-kit/stats-distributions/format";

/** English strings. A translated content file overrides these through `ui`. */
export const POISSON_DISTRIBUTION_CALCULATOR_STRINGS = {
  rateLabel: "Average number of events per unit (rate)",
  lengthLabel: "Length of your interval, in the same units",
  lengthHint: "Leave at 1 if the rate already covers your whole interval.",
  kLabel: "Number of events (k)",
  k2Label: "Upper end of a range (optional)",
  k2Hint: "Adds P(k ≤ X ≤ this value) to the results.",
  lambdaLine: "λ = rate × length = {rate} × {length} = {lambda}",
  results: "Results",
  probability: "Probability",
  meaning: "Meaning",
  value: "Value",
  equal: "exactly {k}",
  atMost: "at most {k}",
  lessThan: "fewer than {k}",
  atLeast: "at least {k}",
  greaterThan: "more than {k}",
  between: "from {a} to {b}, inclusive",
  mean: "Mean (λ)",
  variance: "Variance (λ)",
  sd: "Standard deviation (√λ)",
  normalOk:
    "λ = {lambda} is at least 10, so the normal approximation N({lambda}, {sd}²) is reasonable. With a continuity correction it gives P(X ≤ {k}) ≈ {approx}, against the exact {exact}.",
  normalPoor:
    "λ = {lambda} is below 10, so the distribution is too skewed for a normal approximation — use the exact values above.",
  chartHeading: "Probability distribution",
  chartLabel:
    "Bar chart of P(X = k) for k from {from} to {to}, with the bars your question covers drawn darker",
  tableCaption: "Poisson probabilities for λ = {lambda}, k = {from} to {to}",
  tableLimit:
    "The table and chart are drawn while the distribution fits in about 200 rows (λ up to about 250).",
  enterRate: "Enter a rate greater than 0.",
  enterLength: "Enter an interval length greater than 0.",
  enterLambda: "λ (rate × length) must be at most 10,000.",
  enterK: "Enter k as a whole number of 0 or more.",
} as const;

type Strings = typeof POISSON_DISTRIBUTION_CALCULATOR_STRINGS;

/** Beyond this the incomplete gamma behind the Poisson tails loses precision. */
const MAX_LAMBDA = 10_000;

interface Inputs {
  rate: number;
  length: number;
  lambda: number;
  k: number;
  k2: number | null;
}

function readInputs(
  rateText: string,
  lengthText: string,
  kText: string,
  k2Text: string
): Inputs | keyof Strings {
  const rate = parseNumberInput(rateText);
  if (rate === null || rate <= 0) {
    return "enterRate";
  }
  const length = parseNumberInput(lengthText);
  if (length === null || length <= 0) {
    return "enterLength";
  }
  const lambda = rate * length;
  if (lambda > MAX_LAMBDA) {
    return "enterLambda";
  }
  const k = parseIntegerInput(kText);
  const hasSecond = k2Text.trim() !== "";
  const k2 = hasSecond ? parseIntegerInput(k2Text) : null;
  if (k === null || k < 0 || (hasSecond && (k2 === null || k2 < 0))) {
    return "enterK";
  }
  return { rate, length, lambda, k, k2 };
}

function NormalNote({
  inputs,
  model,
  strings,
  fmt,
}: {
  inputs: Inputs;
  model: DiscreteModel;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const four = (value: number) => fmt(value, { maximumFractionDigits: 4 });
  const sd = Math.sqrt(inputs.lambda);
  if (inputs.lambda < 10) {
    return (
      <Notice>{t(strings.normalPoor, { lambda: four(inputs.lambda) })}</Notice>
    );
  }
  return (
    <Notice>
      {t(strings.normalOk, {
        lambda: four(inputs.lambda),
        sd: four(sd),
        k: fmt(inputs.k),
        approx: four(normalCdf(inputs.k + 0.5, inputs.lambda, sd)),
        exact: four(model.cdf(inputs.k)),
      })}
    </Notice>
  );
}

function Distribution({
  inputs,
  model,
  strings,
  fmt,
}: {
  inputs: Inputs;
  model: DiscreteModel;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const { k, k2 } = inputs;
  const range = useMemo(
    () => tableRange(model, k2 === null ? [k] : [k, k2]),
    [model, k, k2]
  );
  const rows = useMemo(
    () => (range ? distributionTable(model, range[0], range[1]) : []),
    [model, range]
  );
  const geometry = useMemo(
    () => barGeometry(rows.map((row) => ({ k: row.k, p: row.pmf }))),
    [rows]
  );
  if (!range) {
    return <Notice>{strings.tableLimit}</Notice>;
  }
  const low = k2 === null ? k : Math.min(k, k2);
  const high = k2 === null ? k : Math.max(k, k2);
  const highlighted = (value: number) => value >= low && value <= high;
  const span = { from: fmt(range[0]), to: fmt(range[1]) };
  return (
    <>
      <div className={`${CARD} space-y-3`}>
        <h3 className={WIDGET_H3}>{strings.chartHeading}</h3>
        <BarChart
          format={(value) => fmt(value)}
          geometry={geometry}
          highlighted={highlighted}
          label={t(strings.chartLabel, span)}
        />
      </div>
      <DistributionTable
        caption={t(strings.tableCaption, {
          ...span,
          lambda: fmt(inputs.lambda, { maximumFractionDigits: 4 }),
        })}
        fmt={fmt}
        highlighted={highlighted}
        rows={rows}
      />
    </>
  );
}

export default function PoissonDistributionCalculatorWidget() {
  const strings = useToolUi(POISSON_DISTRIBUTION_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [rateText, setRateText] = useState("3");
  const [lengthText, setLengthText] = useState("4");
  const [kText, setKText] = useState("15");
  const [k2Text, setK2Text] = useState("");

  const inputs = readInputs(rateText, lengthText, kText, k2Text);
  const lambda = typeof inputs === "object" ? inputs.lambda : 1;
  const model = useMemo(() => poissonModel(lambda), [lambda]);
  const four = (value: number) => fmt(value, { maximumFractionDigits: 4 });

  return (
    <>
      <section className="grid gap-4 sm:grid-cols-2">
        <TextField
          id="pd-rate"
          label={strings.rateLabel}
          numeric
          onChange={setRateText}
          value={rateText}
        />
        <TextField
          hint={strings.lengthHint}
          id="pd-length"
          label={strings.lengthLabel}
          numeric
          onChange={setLengthText}
          value={lengthText}
        />
        <TextField
          id="pd-k"
          label={strings.kLabel}
          numeric
          onChange={setKText}
          value={kText}
        />
        <TextField
          hint={strings.k2Hint}
          id="pd-k2"
          label={strings.k2Label}
          numeric
          onChange={setK2Text}
          value={k2Text}
        />
      </section>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {typeof inputs === "string" ? (
          <Notice>{strings[inputs]}</Notice>
        ) : (
          <>
            <p className="font-mono text-muted-foreground text-sm">
              {t(strings.lambdaLine, {
                rate: four(inputs.rate),
                length: four(inputs.length),
                lambda: four(inputs.lambda),
              })}
            </p>
            <ProbabilitySummary
              fmt={fmt}
              k={inputs.k}
              labels={strings}
              probabilities={discreteProbabilities(model, inputs.k, inputs.k2)}
            />
            <StatGrid>
              <StatCard label={strings.mean} value={four(model.mean)} />
              <StatCard label={strings.variance} value={four(model.variance)} />
              <StatCard
                label={strings.sd}
                value={four(Math.sqrt(model.variance))}
              />
            </StatGrid>
            <NormalNote
              fmt={fmt}
              inputs={inputs}
              model={model}
              strings={strings}
            />
            <Distribution
              fmt={fmt}
              inputs={inputs}
              model={model}
              strings={strings}
            />
          </>
        )}
      </section>
    </>
  );
}
